import axios from "axios";
import firebase from "./firebase";

const getAuthHeaders = async () => {
  const user = firebase.currentUser();
  const token = user && (await user.getIdToken());
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const buildShareUrl = (shareToken) => `${window.location.origin}/public/global-file/${shareToken}`;

export const listGlobalSecureFiles = async () => {
  try {
    const response = await axios.get(`${process.env.REACT_APP_API_HOST}/admin/global-secure-files`, {
      headers: await getAuthHeaders(),
    });
    return {
      data: (response.data?.data || []).map((file) => ({
        ...file,
        shareUrl: buildShareUrl(file.shareToken),
      })),
    };
  } catch (error) {
    return {
      error: {
        message: error.response?.data?.message || error.message,
        code: error.response?.data?.code,
      },
    };
  }
};

export const uploadGlobalSecureFile = async ({ file }) => {
  try {
    const formData = new FormData();
    formData.append("file", file);

    const response = await axios.post(`${process.env.REACT_APP_API_HOST}/admin/global-secure-files`, formData, {
      headers: {
        ...(await getAuthHeaders()),
        "Content-Type": "multipart/form-data",
      },
    });

    const uploadedFile = response.data?.data;
    return {
      data: uploadedFile
        ? {
            ...uploadedFile,
            shareUrl: buildShareUrl(uploadedFile.shareToken),
          }
        : null,
    };
  } catch (error) {
    return {
      error: {
        message: error.response?.data?.message || error.message,
        code: error.response?.data?.code,
      },
    };
  }
};

export const shareGlobalSecureFile = async ({ shareToken, password }) => {
  try {
    const response = await axios.post(
      `${process.env.REACT_APP_API_HOST}/admin/global-secure-files/${shareToken}/share`,
      { password },
      { headers: await getAuthHeaders() }
    );
    const sharedFile = response.data?.data;
    return {
      data: sharedFile
        ? {
            ...sharedFile,
            shareUrl: buildShareUrl(sharedFile.shareToken),
          }
        : null,
    };
  } catch (error) {
    return {
      error: {
        message: error.response?.data?.message || error.message,
        code: error.response?.data?.code,
      },
    };
  }
};

export const deleteGlobalSecureFile = async (shareToken) => {
  try {
    await axios.delete(`${process.env.REACT_APP_API_HOST}/admin/global-secure-files/${shareToken}`, {
      headers: await getAuthHeaders(),
    });
    return { data: true };
  } catch (error) {
    return {
      error: {
        message: error.response?.data?.message || error.message,
        code: error.response?.data?.code,
      },
    };
  }
};

export const revokeGlobalSecureFileShare = async (shareToken) => {
  try {
    const response = await axios.post(
      `${process.env.REACT_APP_API_HOST}/admin/global-secure-files/${shareToken}/revoke`,
      {},
      { headers: await getAuthHeaders() }
    );
    return { data: response.data?.data };
  } catch (error) {
    return {
      error: {
        message: error.response?.data?.message || error.message,
        code: error.response?.data?.code,
      },
    };
  }
};

export const regenerateGlobalSecureFileShare = async ({ shareToken, password }) => {
  try {
    const response = await axios.post(
      `${process.env.REACT_APP_API_HOST}/admin/global-secure-files/${shareToken}/regenerate-link`,
      { password },
      { headers: await getAuthHeaders() }
    );
    const regeneratedFile = response.data?.data;
    return {
      data: regeneratedFile
        ? {
            ...regeneratedFile,
            shareUrl: buildShareUrl(regeneratedFile.shareToken),
          }
        : null,
    };
  } catch (error) {
    return {
      error: {
        message: error.response?.data?.message || error.message,
        code: error.response?.data?.code,
      },
    };
  }
};

export const getGlobalSecureFileAccessLogs = async (shareToken) => {
  try {
    const response = await axios.get(
      `${process.env.REACT_APP_API_HOST}/admin/global-secure-files/${shareToken}/access-logs`,
      {
        headers: await getAuthHeaders(),
      }
    );
    return { data: response.data?.data || [] };
  } catch (error) {
    return {
      error: {
        message: error.response?.data?.message || error.message,
        code: error.response?.data?.code,
      },
    };
  }
};

export const getPublicGlobalSecureFileInfo = async (shareToken) => {
  try {
    const response = await axios.get(`${process.env.REACT_APP_API_HOST}/public/global-secure-files/${shareToken}`);
    return { data: response.data?.data };
  } catch (error) {
    return {
      error: {
        message: error.response?.data?.message || error.message,
        code: error.response?.data?.code,
      },
    };
  }
};

export const verifyPublicGlobalSecureFileAccess = async ({ shareToken, password }) => {
  try {
    const response = await axios.post(
      `${process.env.REACT_APP_API_HOST}/public/global-secure-files/${shareToken}/verify`,
      { password }
    );
    return { data: response.data?.data };
  } catch (error) {
    return {
      error: {
        message: error.response?.data?.message || error.message,
        code: error.response?.data?.code,
      },
    };
  }
};

export const downloadPublicGlobalSecureFile = async ({ shareToken, accessToken }) => {
  try {
    const response = await axios.get(
      `${process.env.REACT_APP_API_HOST}/public/global-secure-files/${shareToken}/download`,
      {
        headers: {
          "x-access-token": accessToken,
        },
        responseType: "blob",
      }
    );
    return { data: response.data };
  } catch (error) {
    return {
      error: {
        message: error.response?.data?.message || error.message,
        code: error.response?.data?.code,
      },
    };
  }
};

export { buildShareUrl };
